import axios from 'axios'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

/**
 * moduleData — the CURRENT-MODULE LIST cache. `data` is the array of records for the module
 * currently being viewed (with `meta` for pagination), scoped ONLY by `module_id`; there is no
 * per-module namespacing, so navigating to another module overwrites it. ModuleDetail reads this
 * same bucket: `setModuleData({ data: [oneRecord] })` is an inline-edit shortcut that temporarily
 * collapses the list to a single record. A failed fetch sets `status: 'failed'` and `errors` —
 * read those (not `data.length`) to distinguish a failed load from a genuinely empty result.
 * `lastFetchParams` is the thunk's own last argument object (Redux Toolkit's `action.meta.arg`).
 * Pagination and sorting read it to rebuild a customer search (see below) for another page or order.
 */
const initialState = {
  initialMeta: null,
  data: [],
  meta: {},
  status: 'idle',
  errors: null,
  module_id: null,
  lastFetchParams: null
}

// Modules whose `filters` can carry PII (SSN, etc). A filtered fetch for one of these goes
// to POST `{module}/search` (body-encoded) instead of GET, so the value never lands in a
// URL. Unfiltered fetches of these modules are unaffected and stay on the GET index route.
const PII_SEARCHABLE_MODULES = ['customer']

export const fetchModuleData = createAsyncThunk(
  'moduleData/fetchModuleData',
  async ({ accessToken, module_endpoint, preloadedData, spoofedUserId }, { getState }) => {
    const state = getState();
    let spoofedUserIDLocal = spoofedUserId || state?.user?.spoofed_user?.id || null;
    if (preloadedData) return preloadedData
    if (module_endpoint.includes('&filters=')) {
      const filters = module_endpoint.split('&filters=')[1];
      module_endpoint = module_endpoint.split('&filters=')[0];
      module_endpoint += `&filters=${encodeURI(filters)}`;
    }
    if (spoofedUserIDLocal) {
      let spoofQuery = `?spoofUserID=${spoofedUserIDLocal}`
      if (module_endpoint.includes('?')) {
        spoofQuery = spoofQuery.replace('?', '&')
      }
      module_endpoint += spoofQuery
    }
    if (state?.user.filtered_portfolio) {
      if (!module_endpoint.includes('portfolio_id')) {
        let portfolioQuery = `?portfolio_id=${state.user.filtered_portfolio}`
        if (module_endpoint.includes('?')) {
          portfolioQuery = portfolioQuery.replace('?', '&')
        }
        module_endpoint += portfolioQuery
      }
    }

    const [path, queryString] = module_endpoint.split('?')
    if (PII_SEARCHABLE_MODULES.includes(path) && queryString?.includes('filters=')) {
      // spoofUserID and portfolio_id stay on the URL because the backend reads them via a
      // plain query-string lookup (getRequestUser()), which doesn't see JSON body fields.
      // Only the search inputs (filters, sort, per_page, page) move into the body.
      const body = {}
      const urlParams = new URLSearchParams()
      new URLSearchParams(queryString).forEach((value, key) => {
        if (key === 'spoofUserID' || key === 'portfolio_id') {
          urlParams.set(key, value)
        } else {
          body[key] = value
        }
      })
      const postUrl = `${process.env.REACT_APP_API_DOMAIN}/${path}/search` +
        (urlParams.toString() ? `?${urlParams.toString()}` : '')
      const response = await axios.post(
        postUrl,
        JSON.stringify(body),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      return response.data
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_DOMAIN}/${module_endpoint}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    return response.data
  }
)

export const moduleDataSlice = createSlice({
  name: 'moduleData',
  initialState,
  reducers: {
    setModuleData: (state, action) => {
      state.data = action.payload.data
      state.status = 'succeeded'
      state.meta = {}
    },
    // Write a just-saved record back into the list cache. ModuleDetail re-derives its row from
    // this cache whenever `auth` changes (silent token refresh), so without this a saved edit
    // reverts to the pre-save list value. Scoped by module_id because record ids are only
    // unique within a module; a no-op if the cache holds another module or not this record.
    updateModuleDataRecord: (state, action) => {
      const { module_id, record } = action.payload
      if (state.module_id !== module_id) return
      const index = state.data.findIndex((r) => r.id === record.id)
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...record }
      }
    },
    setModuleId: (state, action) => {
      state.module_id = action.payload.module_id
      state.initialMeta = null
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchModuleData.pending, (state, action) => {
        state.status = 'loading'
        state.lastFetchParams = action.meta.arg
      })
      .addCase(fetchModuleData.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.data = action.payload.data
        state.meta = action.payload.meta
        if (state.initialMeta === null) {
          state.initialMeta = action.payload.meta ?? [];
        }
      })
      .addCase(fetchModuleData.rejected, (state, action) => {
        state.status = 'failed'
        // Write `errors` (the key initialState declares); the old `state.error` was never read.
        state.errors = action.error.message
      })
  },
})

export const { setModuleId, setModuleData, updateModuleDataRecord } = moduleDataSlice.actions
export const selectModuleData = (state) => state.moduleData
export default moduleDataSlice.reducer
